import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Assento, Pedido, PedidoStatusCode } from "../utils/interfaces";
import { api } from "../libs/axios";
import { Bilhete } from "../utils/interfaces";

interface PedidoContextProps {
  pedido: Pedido | null;
  bilhetes: Bilhete[];
  setBilhetes: (bilhetes: Bilhete[]) => void;
  listSelects: Assento[];
  setListaLayoutSelect: (assento: Assento) => void;
  removerAssentoLayout: (assento?: string) => void;
  clearLayout: () => void;
  recuperar: () => void;
  reload: () => void;
  save: (pedido: unknown) => void;
}

interface PedidoProviderProps {
  children: ReactNode;
}

export const PedidoContext = createContext({} as PedidoContextProps);

export function PedidoProvider({ children }: PedidoProviderProps) {
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [listSelects, setListSelects] = useState<Assento[]>([]);

  const normalizarPedido = (value: unknown): Pedido | null => {
    if (
      value &&
      typeof value === "object" &&
      "data" in value
    ) {
      return normalizarPedido((value as { data?: unknown }).data);
    }

    if (
      value &&
      typeof value === "object" &&
      Object.prototype.hasOwnProperty.call(value, "id")
    ) {
      const pedido = value as Pedido;
      return {
        ...pedido,
        bilhetes: Array.isArray(pedido.bilhetes) ? pedido.bilhetes : [],
      };
    }

    return null;
  };

  useEffect(() => {
    recuperar();
  }, []);

  const recuperar = () => {
    const carrinho = localStorage.getItem("carrinho");
    if (carrinho) {
      const pedido = normalizarPedido(JSON.parse(carrinho));
      if (pedido?.id) {
        recuperaPedidoServidor(pedido.id);
      } else {
        localStorage.removeItem("carrinho");
      }
    }
  };

  async function recuperaPedidoServidor(id: string) {
    await api
      .get(`/passagens/${id}/carrinho`)
      .then((json) => {
        let pedido = normalizarPedido(json.data);

        if (!pedido || !Object.prototype.hasOwnProperty.call(pedido, "status")) {
          localStorage.removeItem("carrinho");
          setPedido(null);
          return;
        }

        if (![PedidoStatusCode.Aberto, PedidoStatusCode.Reservado].includes(pedido.status)) {
          localStorage.removeItem("carrinho");
          pedido = null;
        }

        setPedido(pedido);
      })
      .catch(() => {
        localStorage.removeItem("carrinho");
      });
  }

  const reload = async () => {
    if (pedido) {
      await recuperaPedidoServidor(pedido.id);
    }
  };

  const save = (pedido: unknown) => {
    const pedidoNormalizado = normalizarPedido(pedido);

    if (!pedidoNormalizado) {
      localStorage.removeItem("carrinho");
      setPedido(null);
      return;
    }

    localStorage.setItem("carrinho", JSON.stringify(pedidoNormalizado));
    setPedido(pedidoNormalizado);
  };

  const setListaLayoutSelect = (assento: Assento) => {
    setListSelects((state) => [...state, assento]);
  };

  const removerAssentoLayout = (assento?: string) => {
    if (!assento) return;

    const assentoNormalizado = String(Number(assento));

    setListSelects((state) => state.filter((item) => (
      String(Number(item.assento)) !== assentoNormalizado
    )));
  };

  const clearLayout = () => {
    setListSelects([]);
  };

  const [bilhetes, setBilhetes] = useState<Bilhete[]>([]);

  return (
    <PedidoContext.Provider
      value={{
        pedido,
        recuperar,
        save,
        reload,
        listSelects,
        setListaLayoutSelect,
        removerAssentoLayout,
        clearLayout,
        bilhetes,
        setBilhetes,
      }}
    >
      {children}
    </PedidoContext.Provider>
  );
}

export const usePedidoContext = () => useContext(PedidoContext);
